<template>
    <ToolbarAbstract class="toolbar-image-flyout">

        <div v-if="isToolMarker" class="content">
            <div class="setting">
                <Heading>size</Heading>
                <v-icon 
                    class="icon icon-pre" 
                    icon="fa-solid fa-circle" 
                    @click="handleMarkerSizeMax" />
                <v-slider
                    hide-details 
                    color="color1"
                    class="slider" 
                    v-model="markerSizeModel"
                    :direction="direction"
                    :min="1"
                    :max="9"
                    :step="1" />
                <v-icon 
                    class="icon icon-post" 
                    icon="fa-solid fa-circle"
                    @click="handleMarkerSizeMin" />
            </div>
        </div>

        <div v-else-if="isToolErase" class="content">
            <div class="setting">
                <Heading>size</Heading>
                <v-icon 
                    class="icon icon-pre" 
                    icon="fa-solid fa-circle" 
                    @click="handleEraseSizeMax" />
                <v-slider
                    hide-details 
                    color="color1"
                    class="slider" 
                    v-model="eraseSizeModel"
                    :direction="direction"
                    :min="1"
                    :max="9"
                    :step="1" />
                <v-icon 
                    class="icon icon-post" 
                    icon="fa-solid fa-circle"
                    @click="handleEraseSizeMin" />
            </div>

            <Separator :comfortable="true" />

            <div v-if="!isSketch" class="setting hide-responsive">
                <Heading>mode</Heading>
                <v-chip-group 
                    column 
                    class="chip-group"
                    color="color1"
                    v-model="eraseModeModel">
                    <v-chip 
                        class="chip" 
                        :value="keyEraseModeAll">
                        {{ keyEraseModeAll }}
                    </v-chip>
                    <v-chip 
                        class="chip" 
                        :value="keyEraseModeMarker">
                        {{ keyEraseModeMarker }}
                    </v-chip>
                    <v-chip 
                        class="chip" 
                        :value="keyEraseModeImage">
                        {{ keyEraseModeImage }}
                    </v-chip>
                </v-chip-group>
            </div>
        </div>

        <div v-else-if="isToolCrop" class="content">

            <div class="setting">
                <Heading>crop</Heading>

                <!-- Apply -->
                <v-btn
                    color="darkbase"
                    elevation="1"
                    @click="handleApply">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        apply crop
                    </v-tooltip>
                    <v-icon class="color-success" icon="fa-solid fa-check" />
                </v-btn>

                <!-- Reject -->
                <v-btn
                    color="darkbase"
                    elevation="1"
                    @click="handleReject">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        reject crop
                    </v-tooltip>
                    <v-icon class="color-error" icon="fa-solid fa-xmark" />
                </v-btn>
            </div>

            <div class="setting">
                <Heading>zoom</Heading>

                <!-- Zoom out -->
                <v-btn
                    color="darkbase"
                    elevation="1"
                    @click="handleZoomOut">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        zoom out
                    </v-tooltip>
                    <v-icon class="color-secondary" icon="fa-solid fa-magnifying-glass-minus" />
                </v-btn>

                <!-- Zoom in -->
                <v-btn
                    color="darkbase"
                    elevation="1"
                    @click="handleZoomIn">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        zoom in
                    </v-tooltip>
                    <v-icon class="color-secondary" icon="fa-solid fa-magnifying-glass-plus" />
                </v-btn>
            </div>

        </div>

    </ToolbarAbstract>
</template>

<script>
import { isSmallMobileResolution } from '@/utils/device'
import ToolbarAbstract from '@/components/common/toolbar/ToolbarAbstract.vue'
import Heading from '@/components/common/toolbar/Heading.vue'
import Separator from '@/components/common/toolbar/Separator.vue'

export default {
    name: 'ToolbarImageFlyout',
    components: {
        ToolbarAbstract,
        Heading,
        Separator
    },
    props: {
		tool: {
			type: String,
			default: 'view',
			validator: value => ['view', 'sketch', 'marker', 'erase', 'crop'].includes(value)
		},
        isSketch: {
            type: Boolean,
            default: false
        },

        markerSize: {
            type: Number,
            default: 5
        },

        eraseSize: {
            type: Number,
            default: 5
        },
        eraseMode: {
            type: String,
            default: 'all'
        }
    },
    data() {
        return {}
    },
    computed: {
        keyEraseModeAll() { return 'all' },
        keyEraseModeMarker() { return 'marker' },
        keyEraseModeImage() { return 'image' },

        isToolMarker() { return this.tool === 'marker' },
        isToolErase() { return this.tool === 'erase' },
        isToolCrop() { return this.tool === 'crop' },

        direction() {
			if (isSmallMobileResolution()) return 'horizontal'
			return 'vertical'
		},

        markerSizeModel: {
            get() { return this.markerSize },
            set(v) { this.$emit('update:markerSize', v) }
        },
        eraseSizeModel: {
            get() { return this.eraseSize },
            set(v) { this.$emit('update:eraseSize', v) }
        },
        eraseModeModel: {
            get() { return this.eraseMode },
            set(v) { this.$emit('update:eraseMode', v) }
        }
    },
    emits: [
        'trigger',
        'update:markerSize',
        'update:eraseSize',
        'update:eraseMode'
    ],
    methods: {
        handleMarkerSizeMax() { this.markerSizeModel = 9 },
        handleMarkerSizeMin() { this.markerSizeModel = 1 },
        handleEraseSizeMax() { this.eraseSizeModel = 9 },
        handleEraseSizeMin() { this.eraseSizeModel = 1 },
        handleApply() { this.$emit('trigger', 'apply') },
        handleReject() { this.$emit('trigger', 'reject') },
        handleZoomOut() { this.$emit('trigger', 'zoom-out') },
        handleZoomIn() { this.$emit('trigger', 'zoom-in') }
    }
}
</script>

<style>
.toolbar-image-flyout .slider .v-slider-track__fill {
    background-color: transparent !important;
}

.toolbar-image-flyout .slider:hover .v-slider-thumb__surface {
	background-color: rgb(var(--v-theme-color1)) !important;
}

.toolbar-image-flyout .v-slide-group__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
}

@media (max-width: 600px) {
    .toolbar-image-flyout .v-slide-group__content {
        flex-direction: row;
    }
}
</style>

<style scoped>
.toolbar-image-flyout .content {
    display: flex;
    height: 100%;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.toolbar-image-flyout .setting {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
}

.toolbar-image-flyout .icon {
    color: rgb(var(--v-theme-darkbase));
}

.toolbar-image-flyout .icon-pre {
	height: 1.5rem;
	display: flex;
    margin-bottom: 5px;
	justify-content: center;
	align-items: flex-end;
    font-size: 0.8rem;
}

.toolbar-image-flyout .icon-pre:hover {
	color: rgb(var(--v-theme-color1));
}

.toolbar-image-flyout .icon-post {
    margin-top: 5px;
    font-size: 2rem;
}

.toolbar-image-flyout .icon-post:hover {
	color: rgb(var(--v-theme-color1));
}

.toolbar-image-flyout .chip {
    width: 100%;
    margin: 0px;
    padding-left: 10px;
    padding-right: 10px;
    font-size: 0.9rem;
    border-radius: 8px;
    color: rgb(var(--v-theme-secondary));
    display: flex;
    align-items: center;
    justify-content: center;
}

@media (max-width: 600px) {
    .toolbar-image-flyout {
        flex-direction: row;
    }

    .toolbar-image-flyout .content {
        flex-direction: row;
        padding-left: 5px;
        padding-right: 5px;
    }

    .toolbar-image-flyout .setting {
        max-height: 30px;
        flex-direction: row;
        justify-content: center;
    }

    .toolbar-image-flyout .setting .v-btn {
        min-height: 30px !important;
        max-height: 30px !important;
    }

    .toolbar-image-flyout .chip {
        width: unset;
    }
}

@media (max-width: 500px) {
    .toolbar-image-flyout .setting.hide-responsive {
        display: none;
    }
}
</style>